import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, CardActionArea, Chip, Dialog, DialogContent, DialogTitle, Grid, IconButton } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import { templateGroups } from "../utils/vertical";
import { resolvePublicAsset } from "../utils/api";

const COLOR_PATTERN = /^(#[0-9a-f]{3,8}|rgba?\(|hsla?\()/i;

function paletteColors(template) {
  const palette = template?.palette && typeof template.palette === "object" ? template.palette : {};
  const values = Object.values(palette).filter((value) => typeof value === "string" && COLOR_PATTERN.test(value.trim()));
  return values.length ? values : [colors.brand.primary, colors.brand.primaryContainer, colors.background.offWhite, colors.text.dark];
}

function previewSource(layout) {
  return resolvePublicAsset(layout?.preview_image || (layout?.id ? `/website-templates/${layout.id}.png` : ""));
}

// Shown when a preview image is missing: a block in the template's palette.
function PaletteFallback({ template, layout, height }) {
  const swatches = paletteColors(template);
  const [ground, accent, surface, ink] = [swatches[0], swatches[1] || swatches[0], swatches[2] || "#fff", swatches[3] || colors.text.dark];
  return (
    <MDBox sx={{ height: height === "auto" ? 420 : height, bgcolor: surface, display: "flex", flexDirection: "column", p: 1.5, gap: 1, overflow: "hidden" }}>
      <MDBox sx={{ height: "38%", borderRadius: 1.5, bgcolor: ground, display: "flex", alignItems: "flex-end", p: 1 }}>
        <MDBox sx={{ height: 8, width: "45%", borderRadius: 1, bgcolor: accent }} />
      </MDBox>
      <MDBox sx={{ display: "flex", gap: 0.75, flex: 1 }}>
        {[0, 1, 2].map((item) => (
          <MDBox key={item} sx={{ flex: 1, borderRadius: 1, bgcolor: "#fff", border: `1px solid ${ink}22` }} />
        ))}
      </MDBox>
      <MDBox sx={{ display: "flex", gap: 0.5 }}>
        {swatches.slice(0, 6).map((swatch) => (
          <MDBox key={swatch} title={swatch} sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: swatch, border: "1px solid rgba(0,0,0,.08)" }} />
        ))}
        <MDTypography variant="caption" sx={{ ml: "auto", color: ink, opacity: 0.7 }}>{layout?.name}</MDTypography>
      </MDBox>
    </MDBox>
  );
}

PaletteFallback.propTypes = { template: PropTypes.object, layout: PropTypes.object, height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };
PaletteFallback.defaultProps = { template: null, layout: null, height: 150 };

function LayoutImage({ template, layout, height }) {
  const src = previewSource(layout);
  const [failedSrc, setFailedSrc] = useState(null);
  if (!src || failedSrc === src) return <PaletteFallback template={template} layout={layout} height={height} />;
  return (
    <MDBox
      component="img"
      src={src}
      alt={`${layout.name} layout preview`}
      loading="lazy"
      onError={() => setFailedSrc(src)}
      sx={{ display: "block", width: "100%", height, objectFit: height === "auto" ? "contain" : "cover", objectPosition: "top", bgcolor: colors.background.offWhite }}
    />
  );
}

LayoutImage.propTypes = { template: PropTypes.object, layout: PropTypes.object.isRequired, height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };
LayoutImage.defaultProps = { template: null, height: 150 };

function LayoutPicker({ templates, templateId, layoutId, onChange, onPreview, disabled }) {
  const [lightbox, setLightbox] = useState(null);
  const template = templates.find((item) => item.id === templateId) || templates[0] || null;

  const chooseTemplate = (next) => {
    if (next.id === template?.id) return;
    onChange({ template_id: next.id, layout_id: next.layouts?.[0]?.id || "" });
  };

  const groups = templateGroups(templates);
  const templateCard = (item) => {
    const selected = item.id === template?.id;
    return (
      <Grid item xs={12} sm={6} md={4} key={item.id}>
        <Card sx={{ border: `${selected ? 2 : 1}px solid ${selected ? colors.brand.primary : colors.border.default}`, boxShadow: "none", height: "100%" }}>
          <CardActionArea disabled={disabled} onClick={() => chooseTemplate(item)} sx={{ p: 1.5, height: "100%", display: "block" }}>
            {item.eyebrow && <MDTypography variant="caption" color="text" textTransform="uppercase" letterSpacing={0.6}>{item.eyebrow}</MDTypography>}
            <MDTypography variant="button" fontWeight="medium" display="block">{item.name}</MDTypography>
            {item.description && <MDTypography variant="caption" color="text" display="block" mt={0.25}>{item.description}</MDTypography>}
            <MDBox display="flex" gap={0.5} mt={1}>
              {paletteColors(item).slice(0, 6).map((swatch) => (
                <MDBox key={swatch} sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: swatch, border: "1px solid rgba(0,0,0,.08)" }} />
              ))}
            </MDBox>
          </CardActionArea>
        </Card>
      </Grid>
    );
  };

  const preview = (layout) => {
    if (onPreview) onPreview(layout, template);
    else setLightbox(layout);
  };

  return (
    <MDBox>
      {templates.length > 1 && (
        groups.length > 1 ? (
          // Travel and real estate in separate, labelled groups: the template
          // decides what kind of website this is.
          groups.map((group) => (
            <MDBox key={group.vertical} mb={2.5} component="section" aria-label={group.label}>
              <MDTypography variant="button" fontWeight="bold" display="block">{group.label}</MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={1}>{group.caption}</MDTypography>
              <Grid container spacing={1.5}>{group.templates.map(templateCard)}</Grid>
            </MDBox>
          ))
        ) : (
          <Grid container spacing={1.5} mb={2.5}>{templates.map(templateCard)}</Grid>
        )
      )}
      {templates.length === 1 && template?.description && (
        <MDTypography variant="body2" color="text" mb={2}>{template.description}</MDTypography>
      )}
      <Grid container spacing={2}>
        {(template?.layouts || []).map((layout) => {
          const selected = layout.id === layoutId;
          return (
            <Grid item xs={12} sm={6} lg={4} key={layout.id}>
              <Card sx={{ border: `${selected ? 2 : 1}px solid ${selected ? colors.brand.primary : colors.border.default}`, boxShadow: "none", overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
                <CardActionArea
                  disabled={disabled}
                  onClick={() => onChange({ template_id: template.id, layout_id: layout.id })}
                  aria-pressed={selected}
                  sx={{ position: "relative" }}
                >
                  <LayoutImage template={template} layout={layout} />
                  {selected && (
                    <CheckCircleIcon sx={{ position: "absolute", top: 8, right: 8, color: colors.brand.primary, bgcolor: "#fff", borderRadius: "50%" }} />
                  )}
                </CardActionArea>
                <MDBox p={1.5} display="flex" flexDirection="column" flexGrow={1}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography variant="button" fontWeight="medium">{layout.name}</MDTypography>
                    {selected && <Chip size="small" label="Selected" sx={{ bgcolor: colors.chip.selectedBackground, color: colors.chip.selectedText }} />}
                  </MDBox>
                  {layout.description && <MDTypography variant="caption" color="text" display="block" mt={0.25}>{layout.description}</MDTypography>}
                  {Array.isArray(layout.sections) && layout.sections.length > 0 && (
                    <MDTypography variant="caption" color="text" display="block" mt={0.5} sx={{ opacity: 0.8 }}>
                      {layout.sections.map((section) => (typeof section === "string" ? section : section?.name || section?.id)).filter(Boolean).join(" · ")}
                    </MDTypography>
                  )}
                  <MDBox mt="auto" pt={1}>
                    <MDButton size="small" variant="text" color="info" sx={{ px: 0 }} onClick={() => preview(layout)}>
                      Preview
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="lg" fullWidth>
        {lightbox && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 6 }}>
              {template?.name} · {lightbox.name}
              <IconButton aria-label="Close preview" onClick={() => setLightbox(null)} sx={{ position: "absolute", right: 12, top: 12 }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <MDBox sx={{ borderRadius: 2, overflow: "hidden", border: `1px solid ${colors.border.default}` }}>
                <LayoutImage template={template} layout={lightbox} height="auto" />
              </MDBox>
              <MDTypography variant="caption" color="text" display="block" mt={1}>
                A live preview with your own brand is available once the website is created.
              </MDTypography>
            </DialogContent>
          </>
        )}
      </Dialog>
    </MDBox>
  );
}

LayoutPicker.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  templateId: PropTypes.string,
  layoutId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  disabled: PropTypes.bool,
};

LayoutPicker.defaultProps = { templateId: "", layoutId: "", onPreview: null, disabled: false };

export default LayoutPicker;
